import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, AlertCircle, Filter, Calendar, ChevronDown, ExternalLink, Phone, Mail, DollarSign, Clock, X, List, Map as MapIcon } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";

// Navigate the admin to the in-portal Messages thread for a given estimate.
// The old implementation had a subtle "wrong person" bug: when the estimate
// had no customer_id, it hit `/api/customers?phone=X`, but that endpoint
// ignores the `phone` param (only accepts `search`) and returned the full
// customer list — the code then picked `customers[0]`, which is whichever
// customer happened to be first, sending admins to a random unrelated
// thread. Fix: always route by phone in that fallback path — Messages.js
// already normalises `?phone=` to the trailing 10 digits and matches
// against every conversation, so the correct thread opens even when the
// estimate's stored customer_id is stale/orphaned.
const openInPortalMessages = (estimate, navigate) => {
  if (!estimate) return;
  const phone = estimate.phone || "";
  const name = estimate.customer_name || estimate.full_name || "";
  const params = new URLSearchParams();
  if (estimate.customer_id) params.set("customer", estimate.customer_id);
  if (phone) params.set("phone", phone);
  if (name) params.set("name", name);
  navigate(`/messages?${params.toString()}`);
};

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Format currency
const formatCurrency = (value) => {
  if (!value) return "$0";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Status badge colors
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  nurturing: "bg-purple-100 text-purple-700",
  followed_up: "bg-amber-100 text-amber-700",
  sent: "bg-cyan-100 text-cyan-700",
  hot: "bg-rose-100 text-rose-600",
};

export default function OpenEstimatesMap() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(true);
  
  const [data, setData] = useState(null);
  // Default to a bounded range — "All Time" was overloading the response and
  // failing to render. Admins can still pick a custom range below.
  const [dateFilter, setDateFilter] = useState("30days");
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [showMissingList, setShowMissingList] = useState(false);
  const [mapView, setMapView] = useState(true);
  const [expandedLeadId, setExpandedLeadId] = useState(null);

  // Inline-text flow (Feb 2026):
  //   - `contactedLeads`  → Set of lead_ids Brett has texted THIS session.
  //     Session-only, no persistence — the goal is a visual "who did I
  //     already reach out to on this map pass?" cue. Contacted pins turn
  //     green so the eye can skip them while route-planning nearby stops.
  //   - `textSheetEstimate` opens a bottom sheet composer on top of the
  //     map so admins never leave the map when texting. Fixes the "text →
  //     back → find pin → repeat" back-and-forth Brett called out.
  //   - `quickTemplates` are the same DB-backed templates from Settings →
  //     Quick Reply Templates. One-tap fills the composer with merge-tag
  //     substitution matching Messages.js semantics.
  const [contactedLeads, setContactedLeads] = useState(new Set());
  const [textSheetEstimate, setTextSheetEstimate] = useState(null);
  const [messageBody, setMessageBody] = useState("");
  const [sending, setSending] = useState(false);
  const [quickTemplates, setQuickTemplates] = useState([]);
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  // Keyed by lead_id so we can flip a single marker's icon after a send
  // without rebuilding the whole map (which would refire the expensive
  // Places geocoding pass and blank the map for ~5 s).
  const markersRef = useRef(new Map());
  const infoWindowRef = useRef(null);
  const placesServiceRef = useRef(null);

  const dateFilterOptions = [
    { value: "7days", label: "Last 7 Days" },
    { value: "30days", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
    { value: "custom", label: "Custom Range…" },
  ];

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
          setUser(response.data);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  const fetchData = useCallback(async () => {
    // Custom range needs both dates before we can query the backend.
    if (dateFilter === "custom" && (!customStart || !customEnd)) {
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const params = { date_filter: dateFilter };
      if (dateFilter === "custom") {
        // Backend already supports `start_date` / `end_date` ISO strings on
        // /estimates/map. Send the day's full window (00:00 → 23:59:59).
        params.start_date = `${customStart}T00:00:00Z`;
        params.end_date = `${customEnd}T23:59:59Z`;
      }
      const response = await axios.get(`${API}/estimates/map`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching map data:", error);
      toast.error("Failed to load estimates");
    } finally {
      setLoading(false);
    }
  }, [dateFilter, customStart, customEnd]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  // Load enabled quick-reply templates once auth is confirmed. Same
  // endpoint the Messages page uses; keeps template edits in one place.
  useEffect(() => {
    if (!isAuthenticated) return;
    const token = localStorage.getItem('session_token');
    axios
      .get(`${API}/quick-replies?enabled_only=true`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setQuickTemplates(Array.isArray(res.data) ? res.data : []))
      .catch(() => setQuickTemplates([]));
  }, [isAuthenticated]);

  // Open the inline text sheet for a given estimate. Clears any prior
  // body so a fresh compose starts blank.
  const openTextSheet = (estimate) => {
    if (!estimate) return;
    setTextSheetEstimate(estimate);
    setMessageBody("");
  };

  const closeTextSheet = () => {
    setTextSheetEstimate(null);
    setMessageBody("");
    setSending(false);
  };

  // Merge-tag substitution mirroring Messages.js so template semantics stay
  // identical across the app. Supports: {first_name}/{firstName}, {name},
  // {booking_link} (instant-quote resume link when the estimate has a lead_id).
  const applyTemplateToSheet = (template) => {
    if (!template || !textSheetEstimate) return;
    const fullName = textSheetEstimate.customer_name || "";
    const firstName = fullName.split(" ")[0] || "there";
    const bookingLink = textSheetEstimate.lead_id
      ? `https://jobs.screenzz.com/instant-estimate?resume=${textSheetEstimate.lead_id}`
      : "";
    let body = template.body || "";
    body = body.replace(/\{first_name\}|\{firstName\}/g, firstName);
    body = body.replace(/\{name\}/g, fullName);
    body = body.replace(/\{booking_link\}/g, bookingLink);
    setMessageBody(body);
  };

  // Send the SMS via /messages/send (same endpoint the Messages page uses).
  // On success: mark the lead as contacted this session, flip its pin
  // green in place, close the sheet, and stay on the map. When the
  // estimate has no customer_id, we still hit /messages/send but pass
  // phone + name so the backend can find-or-create the customer on the
  // fly (see MessageCreate in routers/messages.py). No more falling out
  // to the full Messages page mid-flow.
  const sendTextFromMap = async () => {
    const estimate = textSheetEstimate;
    if (!estimate || !messageBody.trim() || sending) return;
    if (!estimate.phone) {
      toast.error("No phone number on file for this estimate");
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('session_token');
      // Send `customer_id` when available; ALWAYS include phone + name so
      // the backend fallback can find-or-create if the id is missing/stale.
      const payload = {
        customer_id: estimate.customer_id || "",
        message: messageBody.trim(),
        phone: estimate.phone,
        name: estimate.customer_name || estimate.full_name || "",
      };
      await axios.post(`${API}/messages/send`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Session-only contacted state — flip the pin green in place.
      setContactedLeads((prev) => {
        const next = new Set(prev);
        next.add(estimate.lead_id);
        return next;
      });
      const marker = markersRef.current.get(estimate.lead_id);
      if (marker && window.google?.maps) {
        marker.setIcon({
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#16a34a',
          fillOpacity: 0.95,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        });
      }
      toast.success(`Text sent to ${estimate.customer_name || 'customer'}`);
      closeTextSheet();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send text");
      setSending(false);
    }
  };

  // Initialize map when data is loaded
  useEffect(() => {
    if (!data || !mapRef.current || !window.google?.maps) return;
    
    // Initialize map if not already done
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 10,
        center: { lat: 33.4484, lng: -112.0740 }, // Default to Phoenix area
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });
      
      // Use Places Service instead of Geocoder (Places API is already enabled)
      placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstanceRef.current);
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = new Map();
    
    // Geocode and add markers using Places API
    const bounds = new window.google.maps.LatLngBounds();
    let processedCount = 0;
    const totalToProcess = data.mapped_estimates.length;
    
    if (totalToProcess === 0) {
      setLoading(false);
      return;
    }
    
    const addMarker = (estimate, position) => {
      const isContacted = contactedLeads.has(estimate.lead_id);
      const marker = new window.google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        title: estimate.customer_name,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: isContacted
            ? '#16a34a'
            : (estimate.estimate_status === 'hot' ? '#e11d48' : '#7c3aed'),
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });

      marker.addListener('click', () => {
        setSelectedEstimate(estimate);

        const content = `
          <div style="padding: 8px; min-width: 200px;">
            <div style="font-weight: bold; font-size: 14px; margin-bottom: 4px;">${estimate.customer_name}</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 8px;">${estimate.address}</div>
            <div style="font-size: 14px; font-weight: 600; color: #16a34a;">${formatCurrency(estimate.estimate_amount)}</div>
          </div>
        `;
        infoWindowRef.current.setContent(content);
        infoWindowRef.current.open(mapInstanceRef.current, marker);
      });

      markersRef.current.set(estimate.lead_id, marker);
      bounds.extend(position);
    };
    
    const checkComplete = () => {
      processedCount++;
      if (processedCount >= totalToProcess) {
        if (markersRef.current.size > 0) {
          mapInstanceRef.current.fitBounds(bounds);
          if (markersRef.current.size === 1) {
            mapInstanceRef.current.setZoom(14);
          }
        }
      }
    };
    
    data.mapped_estimates.forEach((estimate, index) => {
      if (!estimate.address) {
        checkComplete();
        return;
      }
      
      // Use Places API findPlaceFromQuery (already enabled for autocomplete)
      setTimeout(() => {
        const request = {
          query: estimate.address,
          fields: ['geometry']
        };
        
        placesServiceRef.current.findPlaceFromQuery(request, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results[0]) {
            const location = results[0].geometry.location;
            addMarker(estimate, location);
          }
          checkComplete();
        });
      }, index * 150); // 150ms delay between requests to avoid rate limits
    });
    
  }, [data]);

  // Gate the FULL-SCREEN spinner on FIRST load only. On subsequent refetches
  // (e.g. changing date filter), keep the map div mounted so the existing
  // google.maps.Map instance stays bound to a live DOM node — otherwise
  // markers re-attach to an invisible detached div and the map appears
  // frozen on the initial 30-day view.
  if (isAuthenticated === null || (loading && !data)) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024] mx-auto mb-4"></div>
          <p className="text-zinc-600">Loading estimates map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header user={user} />
      
      <div className="max-w-4xl mx-auto pb-24 p-4">
        {/* Back Button and Header */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-3"
            data-testid="back-to-settings"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-inter">Back to Settings</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-barlow text-2xl font-bold text-zinc-950 uppercase" data-testid="page-title">
                Open Estimates Map
              </h1>
              <p className="font-inter text-zinc-600 text-sm">
                Nurturing estimates with valid addresses
              </p>
            </div>
            
            {/* View Toggle */}
            <div className="flex bg-zinc-200 rounded-lg p-1">
              <button
                onClick={() => setMapView(true)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  mapView ? 'bg-white shadow text-zinc-900' : 'text-zinc-600'
                }`}
              >
                <MapIcon className="w-4 h-4 inline mr-1" />
                Map
              </button>
              <button
                onClick={() => setMapView(false)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  !mapView ? 'bg-white shadow text-zinc-900' : 'text-zinc-600'
                }`}
              >
                <List className="w-4 h-4 inline mr-1" />
                List
              </button>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        {data && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white rounded-xl border border-zinc-200 p-3 text-center">
              <div className="text-2xl font-bold text-zinc-900">{data.total_nurturing}</div>
              <div className="text-xs text-zinc-500">Total Nurturing</div>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{data.mapped_count}</div>
              <div className="text-xs text-zinc-500">Mapped</div>
            </div>
            <button 
              onClick={() => setShowMissingList(true)}
              className="bg-white rounded-xl border border-zinc-200 p-3 text-center hover:border-amber-300 transition-colors"
              disabled={data.missing_count === 0}
            >
              <div className="text-2xl font-bold text-amber-600">{data.missing_count}</div>
              <div className="text-xs text-zinc-500">Missing Address</div>
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          {/* Date Filter */}
          <div className="relative flex-1">
            <button
              onClick={() => setShowDateDropdown(!showDateDropdown)}
              className="w-full bg-white border border-zinc-300 rounded-lg px-4 py-2.5 text-left flex items-center justify-between hover:border-zinc-400 transition-colors"
              data-testid="date-filter"
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-zinc-400" />
                <span className="text-sm font-medium text-zinc-700">
                  {dateFilterOptions.find(o => o.value === dateFilter)?.label}
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${showDateDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showDateDropdown && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg">
                {dateFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setDateFilter(option.value);
                      setShowDateDropdown(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm hover:bg-zinc-50 transition-colors ${
                      dateFilter === option.value ? 'bg-green-50 text-green-700 font-medium' : 'text-zinc-700'
                    }`}
                    data-testid={`date-filter-option-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Custom date range — only visible when "Custom Range" is picked.
            Backend /estimates/map already accepts start_date/end_date ISO
            strings, so we just need to surface inputs here. */}
        {dateFilter === "custom" && (
          <div
            className="flex flex-col sm:flex-row gap-2 mb-4"
            data-testid="map-custom-range-row"
          >
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              max={customEnd || undefined}
              className="flex-1 bg-white border border-zinc-300 rounded-lg px-3 py-2.5 text-sm text-zinc-700 focus:outline-none focus:border-green-500"
              placeholder="Start date"
              data-testid="map-custom-start"
            />
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              min={customStart || undefined}
              className="flex-1 bg-white border border-zinc-300 rounded-lg px-3 py-2.5 text-sm text-zinc-700 focus:outline-none focus:border-green-500"
              placeholder="End date"
              data-testid="map-custom-end"
            />
          </div>
        )}

        {/* Map or List View */}
        {mapView ? (
          <>
            {/* Map Container — wrapped in a positioned parent so we can
                overlay a subtle refreshing indicator on refetches without
                unmounting the map div (which was the root cause of the
                date-filter freeze bug). */}
            <div className="relative">
              <div
                ref={mapRef}
                className="w-full h-[400px] bg-zinc-200 rounded-xl overflow-hidden border border-zinc-300"
                data-testid="estimates-map"
              >
                {!window.google?.maps && (
                  <div className="h-full flex items-center justify-center text-zinc-500">
                    <div className="text-center">
                      <MapPin className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>Loading map...</p>
                    </div>
                  </div>
                )}
              </div>
              {loading && data && (
                <div
                  className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center pointer-events-none rounded-xl"
                  data-testid="estimates-map-refreshing"
                >
                  <div className="bg-white/95 shadow-lg rounded-full px-4 py-2 flex items-center gap-2 border border-zinc-200">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-zinc-200 border-t-[#207024]" />
                    <span className="text-sm text-zinc-700 font-medium">Refreshing…</span>
                  </div>
                </div>
              )}
            </div>

            {/* Selected Estimate Card */}
            {selectedEstimate && (
              <div className="mt-4 bg-white rounded-xl border border-zinc-200 p-4 shadow-sm" data-testid="selected-estimate-card">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-bold text-zinc-900">{selectedEstimate.customer_name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[selectedEstimate.estimate_status] || 'bg-zinc-100 text-zinc-600'}`}>
                        {selectedEstimate.estimate_status || 'new'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-600">{selectedEstimate.address}</p>
                    <div className="flex items-center gap-3 text-sm mt-1">
                      <span className="font-semibold text-green-600 text-lg">{formatCurrency(selectedEstimate.estimate_amount)}</span>
                      <span className="text-zinc-400 text-xs">{formatDate(selectedEstimate.created_at)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedEstimate(null)}
                    className="p-1.5 text-zinc-400 hover:text-zinc-600 flex-shrink-0"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Line Items - what they have an estimate for */}
                {selectedEstimate.line_items && selectedEstimate.line_items.length > 0 ? (
                  <div className="border-t border-zinc-100 pt-3 space-y-1.5" data-testid="estimate-line-items">
                    {selectedEstimate.line_items.map((li, idx) => (
                      <div key={idx} className="flex items-baseline justify-between gap-2 text-sm">
                        <div className="flex-1 min-w-0">
                          <span className="text-zinc-800">{li.name || "Item"}</span>
                          {li.quantity > 1 && (
                            <span className="text-xs text-zinc-400 ml-1">× {li.quantity}</span>
                          )}
                        </div>
                        <span className="text-zinc-700 font-medium flex-shrink-0">
                          {formatCurrency(li.line_total ?? li.total ?? (li.unit_price || 0) * (li.quantity || 1))}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-t border-zinc-100 pt-3 text-xs text-zinc-400 italic">
                    No line items recorded for this estimate.
                  </div>
                )}

                {/* Quick actions: Call / Text — stay on the map */}
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {selectedEstimate.phone ? (
                    <>
                      <a
                        href={`tel:${selectedEstimate.phone}`}
                        data-testid="estimate-call-button"
                        className="flex items-center justify-center gap-1.5 py-2.5 bg-[#207024] hover:bg-[#185a1c] text-white font-semibold rounded-lg text-sm"
                      >
                        <Phone className="w-4 h-4" />
                        Call
                      </a>
                      <button
                        type="button"
                        onClick={() => openTextSheet(selectedEstimate)}
                        data-testid="estimate-sms-button"
                        className="flex items-center justify-center gap-1.5 py-2.5 bg-white border-2 border-[#207024] text-[#207024] hover:bg-[#207024]/5 font-semibold rounded-lg text-sm"
                      >
                        <MessageSquare className="w-4 h-4" />
                        Text
                      </button>
                    </>
                  ) : (
                    <div className="col-span-2 text-center text-xs text-zinc-400 py-2">
                      No phone number on file
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          /* List View */
          <div className="space-y-3">
            {data?.mapped_estimates.map((estimate) => {
              const isExpanded = expandedLeadId === estimate.lead_id;
              return (
                <div
                  key={estimate.lead_id}
                  className="bg-white rounded-xl border border-zinc-200 overflow-hidden transition-colors"
                  data-testid={`list-estimate-${estimate.lead_id}`}
                >
                  <button
                    type="button"
                    onClick={() => setExpandedLeadId(isExpanded ? null : estimate.lead_id)}
                    className="w-full text-left p-4 hover:bg-zinc-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-bold text-zinc-900">{estimate.customer_name}</h3>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[estimate.estimate_status] || 'bg-zinc-100 text-zinc-600'}`}>
                            {estimate.estimate_status || 'new'}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-600 flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{estimate.address}</span>
                        </p>
                        <div className="flex items-center gap-4 text-sm mt-2">
                          <span className="font-semibold text-green-600">{formatCurrency(estimate.estimate_amount)}</span>
                          <span className="text-zinc-500 flex items-center gap-1 text-xs">
                            <Clock className="w-3 h-3" />
                            {formatDate(estimate.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(estimate.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 text-zinc-500 hover:bg-zinc-100 rounded-lg"
                          title="Open in Google Maps"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <ChevronDown
                          className={`w-5 h-5 text-zinc-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        />
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-zinc-100 bg-zinc-50/40" data-testid={`expanded-${estimate.lead_id}`}>
                      {/* Line items */}
                      {estimate.line_items && estimate.line_items.length > 0 ? (
                        <div className="pt-3 space-y-1.5">
                          {estimate.line_items.map((li, idx) => (
                            <div key={idx} className="flex items-baseline justify-between gap-2 text-sm">
                              <div className="flex-1 min-w-0 text-zinc-800">
                                {li.name || "Item"}
                                {li.quantity > 1 && (
                                  <span className="text-xs text-zinc-400 ml-1">× {li.quantity}</span>
                                )}
                              </div>
                              <span className="text-zinc-700 font-medium flex-shrink-0">
                                {formatCurrency(li.line_total ?? li.total ?? (li.unit_price || 0) * (li.quantity || 1))}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="pt-3 text-xs text-zinc-400 italic">
                          No line items recorded for this estimate.
                        </div>
                      )}

                      {/* Quick actions */}
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        {estimate.phone ? (
                          <>
                            <a
                              href={`tel:${estimate.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center justify-center gap-1.5 py-2.5 bg-[#207024] hover:bg-[#185a1c] text-white font-semibold rounded-lg text-sm"
                            >
                              <Phone className="w-4 h-4" />
                              Call
                            </a>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openTextSheet(estimate);
                              }}
                              data-testid={`list-estimate-sms-${estimate.lead_id}`}
                              className="flex items-center justify-center gap-1.5 py-2.5 bg-white border-2 border-[#207024] text-[#207024] hover:bg-[#207024]/5 font-semibold rounded-lg text-sm"
                            >
                              <MessageSquare className="w-4 h-4" />
                              Text
                            </button>
                          </>
                        ) : (
                          <div className="col-span-2 text-center text-xs text-zinc-400 py-2">
                            No phone number on file
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {data?.mapped_estimates.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No estimates with valid addresses found</p>
              </div>
            )}
          </div>
        )}

        {/* Legend */}
        {mapView && data?.mapped_count > 0 && (
          <div className="mt-4 flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-purple-500"></div>
              <span className="text-zinc-600">Nurturing</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-rose-500"></div>
              <span className="text-zinc-600">Hot Lead</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-600"></div>
              <span className="text-zinc-600">Contacted</span>
            </div>
          </div>
        )}
      </div>

      {/* Inline Text Sheet — bottom drawer with composer + template chips
          + send. Keeps admins on the map (no route change) so they can
          hammer through nearby estimates in one pass. Contacted pins turn
          green in place after send. */}
      {textSheetEstimate && (
        <div
          className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/40"
          onClick={closeTextSheet}
          data-testid="text-sheet-backdrop"
        >
          <div
            className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="text-sheet"
          >
            <div className="sticky top-0 bg-white border-b border-zinc-100 px-5 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-bold text-zinc-900 truncate">
                  Text {textSheetEstimate.customer_name || "Customer"}
                </h3>
                <p className="text-xs text-zinc-500 truncate">
                  {textSheetEstimate.phone || "No phone on file"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeTextSheet}
                aria-label="Close"
                className="p-1.5 text-zinc-400 hover:text-zinc-600 flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Quick template chips — one tap fills the composer. Horiz
                  scroll so we don't wrap when Brett has 6-8 templates. */}
              {quickTemplates.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                    Quick Templates
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    {quickTemplates.map((t) => (
                      <button
                        key={t.template_id}
                        type="button"
                        onClick={() => applyTemplateToSheet(t)}
                        className="flex-shrink-0 px-3 py-1.5 text-sm font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-full border border-zinc-200"
                        data-testid={`text-sheet-template-${t.template_id}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <textarea
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  placeholder="Type a message..."
                  rows={5}
                  className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2.5 focus:border-[#207024] focus:outline-none resize-none text-sm"
                  data-testid="text-sheet-body"
                />
                <div className="text-[11px] text-zinc-400 mt-1 text-right">
                  {messageBody.length} chars
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={sendTextFromMap}
                  disabled={!messageBody.trim() || sending}
                  data-testid="text-sheet-send"
                  className="flex-1 py-3 bg-[#207024] hover:bg-[#185a1c] disabled:bg-zinc-300 text-white font-semibold rounded-lg"
                >
                  {sending ? "Sending…" : "Send Text"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const estimate = textSheetEstimate;
                    closeTextSheet();
                    openInPortalMessages(estimate, navigate);
                  }}
                  className="flex-1 py-3 bg-white border-2 border-zinc-200 hover:bg-zinc-50 text-zinc-700 font-semibold rounded-lg text-sm"
                >
                  Open Full Thread
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Missing Address Modal */}
      {showMissingList && data?.missing_address_estimates && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Estimates Missing Address</h3>
                <p className="text-sm text-zinc-500">{data.missing_count} estimates need addresses</p>
              </div>
              <button
                onClick={() => setShowMissingList(false)}
                className="p-2 hover:bg-zinc-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-auto max-h-[60vh]">
              {data.missing_address_estimates.length > 0 ? (
                <div className="space-y-3">
                  {data.missing_address_estimates.map((estimate) => (
                    <div 
                      key={estimate.lead_id}
                      className="bg-zinc-50 rounded-lg p-3 border border-zinc-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-zinc-900">{estimate.customer_name}</h4>
                          <div className="flex items-center gap-3 text-sm text-zinc-500 mt-1">
                            <span>{formatCurrency(estimate.estimate_amount)}</span>
                            <span>{formatDate(estimate.created_at)}</span>
                          </div>
                          {estimate.address && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Partial: {estimate.address}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setShowMissingList(false);
                            navigate(`/estimates?search=${encodeURIComponent(estimate.customer_name)}`);
                          }}
                          className="px-3 py-1.5 bg-amber-100 text-amber-700 text-sm font-semibold rounded-lg hover:bg-amber-200"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>All estimates have valid addresses!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNav activeTab="settings" user={user} />
    </div>
  );
}
